/**
 * `<RosterAvatar>` — the hex beside each pilot in ROSTER.app ({@link GmRoster}) and SQUAD
 * ({@link SquadApp}): the pilot's token art, clipped to the hex by `.gms-roster-avatar`, or the
 * callsign letter when there is none.
 *
 * Every row here is a pilot (mechs are the chips beneath, which already carry their own art), so
 * the art is the board's pilot rung: {@link tokenImageFor} resolved as a pilot-kind unit sourced
 * from this roster pilot — the same uploaded portrait a deployed on-foot pilot wears on the map.
 * Only the `pilot-portrait` tier shows. The stock `pilot.png` the board falls back to is the same
 * image for everyone, with a red frame baked in, so here the letter tells pilots apart better.
 */
import type { PilotData } from "@lancer-vtt/domain";
import { tokenImageFor } from "../../lib/tokenArt";
import { AvatarImage } from "./PilotAvatar";

/** First letter of the callsign, uppercased, or `?`. */
function initial(callsign: string | undefined): string {
  return (callsign ?? "").trim().charAt(0).toUpperCase() || "?";
}

export function RosterAvatar({ pilot }: { pilot: Pick<PilotData, "id" | "callsign" | "image" | "mechs"> }) {
  const letter = initial(pilot.callsign);
  const token = tokenImageFor(
    { id: pilot.id, kind: "pilot", sourceRef: pilot.id },
    { pilots: { [pilot.id]: pilot }, isMasked: () => false },
  );
  const url = token.tier === "pilot-portrait" ? token.url : undefined;
  return (
    <span className="gms-roster-avatar">
      {url ? <AvatarImage src={url} alt="" className="gms-roster-avatar-img" fallback={letter} /> : letter}
    </span>
  );
}
